import type { DrawRequestDTO, DrawResponseDTO, Orientation } from '@lilith-arcana-os/domain';

import { ANUAdapter } from './anu-adapter';
import { buildDrawnCards, resolveBootstrapSpread } from './bootstrap-draw';
import { BrowserEntropyEngine } from './browser-entropy-engine';
import {
  EntropyNetworkError,
  ServerEntropyClient,
} from './server-entropy-client';
import { drawOrientations } from './orientation';

export interface EntropyFacadeConfig {
  preferServer: boolean;
  allowOfflineFallback: boolean;
  allowANUAugmentation: boolean;
  restBase?: string;
  nonce?: string;
  serverClient?: ServerEntropyClient;
  entropySource?: 'WEB_CRYPTO' | 'CSPRNG_LOCAL';
  anuAdapter?: ANUAdapter;
}

const DEFAULT_BAKER_ROUNDS = 3;

export class EntropyFacade {
  readonly #config: EntropyFacadeConfig;

  public constructor(config: EntropyFacadeConfig) {
    this.#config = config;
  }

  public async drawHosted(request: DrawRequestDTO): Promise<DrawResponseDTO> {
    const client =
      this.#config.serverClient ??
      new ServerEntropyClient({
        restBase: this.#config.restBase ?? '/wp-json/lilith-arcana/v1',
        nonce: this.#config.nonce ?? '',
      });

    return client.draw(request);
  }

  public async drawOffline(request: DrawRequestDTO, deck: number[]): Promise<DrawResponseDTO> {
    const spread = resolveBootstrapSpread(request.spreadId);

    if (deck.length < spread.cards) {
      throw new RangeError(`Deck must contain at least ${spread.cards} cards for spread ${spread.id}.`);
    }

    const engine = new BrowserEntropyEngine(this.#config.entropySource ?? 'WEB_CRYPTO');
    const shuffledDeck = await engine.bakersTransform(deck, DEFAULT_BAKER_ROUNDS);
    const drawnCardIds = shuffledDeck.slice(0, spread.cards);
    const orientations: Orientation[] = await drawOrientations(engine, spread.cards);
    const anuBytes =
      this.#config.allowANUAugmentation && this.#config.anuAdapter
        ? await this.#config.anuAdapter.fetchUint8(16)
        : null;

    return {
      cards: buildDrawnCards({
        cardIds: drawnCardIds,
        orientations,
        spreadId: request.spreadId,
      }),
      audit: await engine.generateAudit({
        drawnCardIds,
        bakerRounds: DEFAULT_BAKER_ROUNDS,
        question: request.question,
        spreadId: request.spreadId,
        sourceLabel: anuBytes ? 'ANU_AUGMENTED' : null,
      }),
    };
  }

  public async drawAuto(request: DrawRequestDTO, deck: number[]): Promise<DrawResponseDTO> {
    if (!this.#config.preferServer) {
      return this.drawOffline(request, deck);
    }

    try {
      return await this.drawHosted(request);
    } catch (error) {
      if (!(error instanceof EntropyNetworkError) || !this.#config.allowOfflineFallback) {
        throw error;
      }

      return this.drawOffline(request, deck);
    }
  }
}
